import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, Minimize2 } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { ui } from "../../../components/ui";
import {
  fetchServices,
  fetchRoleById,
  createRole,
  updateRole,
  assignRoleServices,
  updateRoleServices } from
"../../../services/rolesPermissionsService";
import PermissionTree from "../components/PermissionTree";
import SelectionSummary from "../components/SelectionSummary";
import { UnsavedModal, ZeroPermsModal } from "../components/Modals";
import { TOAST_KEY } from "./RolesListPage";
import "../styles/roles-permissions.css";

export function CreateEditRolePage({ roleId }) {
  const navigate = useNavigate();
  const isEditMode = Boolean(roleId);
  const [permissionMaster, setPermissionMaster] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const ALL_PERM_IDS = useMemo(() => permissionMaster.flatMap((s) => s.modules.flatMap((m) => m.sub_modules.flatMap((sm) => sm.permissions.map((p) => p.id)))), [permissionMaster]);

  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [isAdminRole, setIsAdminRole] = useState(false);
  const [expandedServices, setExpandedServices] = useState(new Set());
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [expandedSubModules, setExpandedSubModules] = useState(new Set());
  const [treeSearch, setTreeSearch] = useState("");
  const [nameError, setNameError] = useState("");
  const [apiError, setApiError] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingRole, setLoadingRole] = useState(isEditMode);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [showZeroPerms, setShowZeroPerms] = useState(false);
  const [pendingNavigate, setPendingNavigate] = useState(null);
  const initialStateRef = useRef({ roleName: "", description: "", selected: new Set() });

  const isDirty = () => {
    const init = initialStateRef.current;
    if (roleName !== init.roleName || description !== init.description || selected.size !== init.selected.size) return true;
    for (const id of selected) if (!init.selected.has(id)) return true;
    return false;
  };

  useEffect(() => {
    fetchServices()
    .then((services) => {
      setPermissionMaster(services);
      setExpandedServices(new Set(services.map((s) => s.id)));
      setExpandedModules(new Set(services.flatMap((s) => s.modules.map((m) => m.id))));
      setExpandedSubModules(new Set(services.flatMap((s) => s.modules.flatMap((m) => m.sub_modules.map((sm) => sm.id)))));
    })
    .catch(() => setApiError("Failed to load permission services."))
    .finally(() => setLoadingServices(false));
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    fetchRoleById(roleId)
    .then((role) => {
      setRoleName(role.name);
      setDescription(role.description);
      const permSet = new Set(role.permissions);
      setSelected(permSet);
      setIsAdminRole(role.type === "System");
      initialStateRef.current = { roleName: role.name, description: role.description, selected: new Set(permSet) };
    })
    .catch(() => setApiError("Failed to load role."))
    .finally(() => setLoadingRole(false));
  }, [roleId, isEditMode]);

  const togglePermission = useCallback((permId) => setSelected((prev) => {
    const next = new Set(prev);if (next.has(permId)) next.delete(permId);else next.add(permId);return next;
  }), []);
  const toggleSubModule = useCallback((_service, _mod, sm) => setSelected((prev) => {
    const ids = sm.permissions.map((p) => p.id);const allIn = ids.every((id) => prev.has(id));const next = new Set(prev);if (allIn) ids.forEach((id) => next.delete(id));else ids.forEach((id) => next.add(id));return next;
  }), []);
  const toggleModule = useCallback((_service, mod) => setSelected((prev) => {
    const ids = mod.sub_modules.flatMap((sm) => sm.permissions.map((p) => p.id));const allIn = ids.every((id) => prev.has(id));const next = new Set(prev);if (allIn) ids.forEach((id) => next.delete(id));else ids.forEach((id) => next.add(id));return next;
  }), []);
  const toggleService = useCallback((service) => setSelected((prev) => {
    const ids = service.modules.flatMap((m) => m.sub_modules.flatMap((sm) => sm.permissions.map((p) => p.id)));const allIn = ids.every((id) => prev.has(id));const next = new Set(prev);if (allIn) ids.forEach((id) => next.delete(id));else ids.forEach((id) => next.add(id));return next;
  }), []);
  const toggleAll = useCallback(() => setSelected((prev) => prev.size === ALL_PERM_IDS.length ? new Set() : new Set(ALL_PERM_IDS)), [ALL_PERM_IDS]);
  const toggleServiceExpand = useCallback((svcId) => setExpandedServices((prev) => {const next = new Set(prev);if (next.has(svcId)) next.delete(svcId);else next.add(svcId);return next;}), []);
  const toggleModuleExpand = useCallback((modId) => setExpandedModules((prev) => {const next = new Set(prev);if (next.has(modId)) next.delete(modId);else next.add(modId);return next;}), []);
  const toggleSubModuleExpand = useCallback((smId) => setExpandedSubModules((prev) => {const next = new Set(prev);if (next.has(smId)) next.delete(smId);else next.add(smId);return next;}), []);
  const collapseAll = () => {setExpandedServices(new Set());setExpandedModules(new Set());setExpandedSubModules(new Set());};

  const safeNavigate = (to) => {
    if (isDirty()) {
      setPendingNavigate(to);
      setShowUnsaved(true);
      return;
    }
    navigate(to);
  };

  const doSave = async () => {
    setSaving(true);
    setApiError("");
    try {
      const permsArray = isAdminRole ? ALL_PERM_IDS : [...selected];
      if (isEditMode) {
        await updateRole(roleId, { name: roleName, description, permissions: permsArray });
        const initialSelected = initialStateRef.current.selected;
        const toEnable = permsArray.filter((pid) => !initialSelected.has(pid));
        const toDisable = [...initialSelected].filter((pid) => !selected.has(pid));
        const changes = [...toEnable.map((pid) => ({ id: pid, isEnabled: true })), ...toDisable.map((pid) => ({ id: pid, isEnabled: false }))];
        if (changes.length > 0) await updateRoleServices(Number(roleId), changes);
      } else {
        const newRole = await createRole({ name: roleName, description, permissions: permsArray });
        await assignRoleServices(newRole.id, permsArray);
      }
      sessionStorage.setItem(TOAST_KEY, JSON.stringify({ message: isEditMode ? `Role "${roleName}" updated successfully.` : `Role "${roleName}" created successfully.`, type: "success" }));
      navigate("/hrms-team/config/roles-services");
    } catch (err) {
      if (err.message.toLowerCase().includes("already exists")) setNameError(err.message);else
      setApiError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const validateAndSave = () => {
    const trimmed = roleName.trim();
    if (!trimmed) {
      setNameError("Role name is required.");
      return;
    }
    setNameError("");
    setApiError("");
    if (selected.size === 0 && !isAdminRole) {
      setShowZeroPerms(true);
      return;
    }
    doSave();
  };

  if (loadingServices || loadingRole) {
    return (
      <AppLayout>
        <div className="py-12 text-center text-sm text-[var(--color-text-secondary)]"><Trans i18nKey="roles.loadingRole" /></div>
      </AppLayout>);

  }

  const pageTitle = isEditMode ? "Edit Role" : "Create Role";

  return (
    <AppLayout
      overlay={
      <>
          {showUnsaved && <UnsavedModal onDiscard={() => {setShowUnsaved(false);if (pendingNavigate) navigate(pendingNavigate);}} onStay={() => {setShowUnsaved(false);setPendingNavigate(null);}} />}
          {showZeroPerms && <ZeroPermsModal onSave={() => {setShowZeroPerms(false);doSave();}} onCancel={() => setShowZeroPerms(false)} />}
        </>
      }>
      
      <div className="mb-5 max-w-[1200px]">
        <PageBackLink to="/hrms-team/config/roles-services" />
        <h1 className={ui.pageTitle}>{pageTitle}</h1>
      </div>

      <div className="max-w-[1200px]">
        {apiError &&
        <div className="flex items-center gap-2 rounded-xl border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 px-3.5 py-2.5 text-[13px] text-[var(--color-error)] mb-4">
            {apiError}
          </div>
        }
        {isAdminRole &&
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-800 mb-4">
            <Trans i18nKey="roles.the" /> <strong><Trans i18nKey="roles.admin" /></strong> <Trans i18nKey="roles.roleIsASystemRoleItsNameAnd" />
          </div>
        }

        <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden p-4 sm:p-6 mb-5">
          <h3 className="text-lg font-medium text-foreground mb-4"><Trans i18nKey="roles.roleDetails" /></h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground">
                <Trans i18nKey="roles.roleName" /> <span className="text-[var(--color-error)]">*</span>
              </span>
              <input
                type="text"
                className={`h-9 rounded-xl border px-3 text-sm text-foreground outline-none transition disabled:bg-[var(--color-workspace)]/40 disabled:text-[var(--color-text-secondary)] disabled:cursor-not-allowed ${
                nameError ?
                "border-[var(--color-error)] focus:ring-2 focus:ring-[var(--color-error)]/10" :
                "border-[var(--color-text-border)] focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/10"}`
                }
                placeholder={i18n.t("examples.eGHrManagerFieldTechnician")}
                value={roleName}
                disabled={isAdminRole || isEditMode}
                onChange={(e) => {setRoleName(e.target.value);setNameError("");}} />
              
              {nameError && <p className="text-xs text-[var(--color-error)] m-0">{nameError}</p>}
            </label>
            {!isEditMode &&
            <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground"><Trans i18nKey="roles.descriptionOptional" /></span>
                <input
                type="text"
                className="h-9 rounded-xl border border-[var(--color-text-border)] px-3 text-sm text-foreground outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/10 transition disabled:bg-[var(--color-workspace)]/40 disabled:text-[var(--color-text-secondary)] disabled:cursor-not-allowed"
                placeholder={i18n.t("roles.shortDescriptionAboutThisRole")}
                value={description}
                disabled={isAdminRole}
                onChange={(e) => setDescription(e.target.value)} />
              
              </label>
            }
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden p-4 sm:p-6 mb-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
            <h3 className="text-lg font-medium text-foreground m-0"><Trans i18nKey="roles.permissions" /></h3>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--color-text-secondary)] pointer-events-none" />
                <input
                  type="text"
                  className="h-9 w-full sm:w-52 rounded-xl border border-[var(--color-text-border)] pl-9 pr-3 text-sm text-foreground outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/10 transition"
                  placeholder={i18n.t("formActions.searchPermissions")}
                  value={treeSearch}
                  onChange={(e) => setTreeSearch(e.target.value)} />
                
              </div>
              <button
                type="button"
                onClick={collapseAll}
                className="inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-xl border border-[var(--color-text-border)] bg-[var(--color-workspace)]/40 text-xs font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]/40 transition">
                
                <Minimize2 className="h-3.5 w-3.5" />
                <Trans i18nKey="formActions.collapseAll" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5 items-start">
            <div className="min-w-0">
              <PermissionTree
                permissionMaster={permissionMaster}
                selected={selected}
                onTogglePermission={togglePermission}
                onToggleSubModule={toggleSubModule}
                onToggleModule={toggleModule}
                onToggleService={toggleService}
                onToggleAll={toggleAll}
                searchQuery={treeSearch}
                expandedServices={expandedServices}
                expandedModules={expandedModules}
                expandedSubModules={expandedSubModules}
                onToggleServiceExpand={toggleServiceExpand}
                onToggleModuleExpand={toggleModuleExpand}
                onToggleSubModuleExpand={toggleSubModuleExpand}
                isAdminRole={isAdminRole} />
              
            </div>
            <div className="lg:sticky lg:top-20">
              <SelectionSummary permissionMaster={permissionMaster} selected={isAdminRole ? new Set(ALL_PERM_IDS) : selected} />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2.5 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={() => safeNavigate("/hrms-team/config/roles-services")}
            disabled={saving}
            className="h-11 px-5 rounded-xl border-[var(--color-text-border)] text-foreground hover:bg-[var(--color-workspace)]/40">
            <Trans i18nKey="formActions.cancel" />
          
          </Button>
          <Button
            type="button"
            onClick={validateAndSave}
            disabled={saving || (isAdminRole && isEditMode)}
            className="h-11 px-5 rounded-xl bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]">
            
            {saving ? "Saving..." : isEditMode ? "Save Changes" : "Save Role"}
          </Button>
        </div>
      </div>
    </AppLayout>);

}

export function EditRoleRoutePage() {
  const { id } = useParams();
  return <CreateEditRolePage roleId={id} />;
}
